import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BookOpen, Loader2, Rss } from "lucide-react";
import { API } from "../lib/constants";

export default function Resources() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("fallback");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/blog/posts`, { timeout: 12000 });
        if (!cancelled) {
          setPosts(res.data?.posts || []);
          setSource(res.data?.source || "fallback");
        }
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="resources-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">
              Resources & Insights
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.98] text-slate-900">
              Field-tested ideas <br />
              <span className="font-editorial italic font-medium text-slate-700">on brand revitalization.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-5">
              Frameworks, case studies, and economic models from the trenches of mature and tail-end
              brand growth in India.
            </p>
            <a
              href="https://blog.medstry.in"
              target="_blank"
              rel="noreferrer"
              data-testid="resources-visit-blog"
              className="inline-flex items-center gap-2 btn-primary px-5 py-3 text-sm font-semibold"
            >
              Visit the Medstry Blog <ArrowUpRight className="h-4 w-4" />
            </a>
            {source === "live" && (
              <div className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-teal-700">
                <Rss className="h-3.5 w-3.5" /> Live from Medstry Blog
              </div>
            )}
          </div>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
            {posts.map((p, i) => (
              <a
                key={p.link || i}
                href={p.link || "#"}
                target={p.link ? "_blank" : undefined}
                rel={p.link ? "noreferrer" : undefined}
                data-testid={`resource-${i}`}
                className="group bg-white border border-slate-200 overflow-hidden hover:border-slate-900 transition-colors flex flex-col"
              >
                {p.image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] bg-slate-50 flex items-center justify-center">
                    <BookOpen className="h-8 w-8 text-slate-300" strokeWidth={1.5} />
                  </div>
                )}
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                      {p.tag || p.category || "Insight"}
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors" />
                  </div>
                  <h3 className="font-display text-xl md:text-[22px] font-bold text-slate-900 leading-snug mb-3">
                    {p.title}
                  </h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                  {p.date && (
                    <div className="text-xs text-slate-400 mt-4 pt-4 border-t border-slate-100">
                      {p.date}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 p-10 md:p-14 text-center">
            <BookOpen className="h-8 w-8 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-bold text-slate-900 mb-2">
              Insights are being prepared.
            </h3>
            <p className="text-slate-600 text-sm max-w-md mx-auto">
              We're curating long-form pieces on brand lifecycle, India extra-urban pharma, and field
              force economics. Check back shortly.
            </p>
          </div>
        )}

        {/* Subscribe CTA */}
        <section className="mt-20 bg-[#0B0F19] text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-300 mb-3">
                Editorial updates
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold mb-3 leading-tight">
                Our latest perspectives on mature-brand growth — delivered first.
              </h3>
              <p className="text-slate-300 text-[15px] max-w-2xl">
                Drop us a note and we'll send fresh case studies and frameworks as they publish.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/#contact" className="inline-flex items-center gap-2 btn-accent px-6 py-3.5 text-sm font-semibold">
                Get Notified
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
